import { useMemo, useState } from 'react';
import {
  CheckIcon,
  SearchIcon,
  UserPlusIcon,
  UserXIcon,
  XIcon,
} from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAssignablePeople } from '@/features/identity/hooks/useAssignablePeople';
import type { UserSummary, WorkspaceRole } from '@/lib/api/generated/model';
import { cn } from '@/lib/utils';
import { UserAvatar } from '@/shared/components/UserAvatar';

import { useAddMember } from '../hooks/useWorkspaceMembers';

/** "Add 3 people", "Add 1 person": what the button will do, counted. */
function addPeopleLabel(count: number): string {
  if (count === 0) return 'Add people';
  return count === 1 ? 'Add 1 person' : `Add ${String(count)} people`;
}

/**
 * The toast once some were added. One name reads as before (spec 186); two are
 * named; past that, a count, since a list of names in a toast is not read.
 */
function addedSentence(names: readonly string[], role: WorkspaceRole): string {
  const as =
    role === 'owner'
      ? names.length === 1
        ? ' as an owner'
        : ' as owners'
      : '';
  if (names.length === 1) return `${names[0] ?? ''} was added${as}.`;
  if (names.length === 2) {
    return `${names[0] ?? ''} and ${names[1] ?? ''} were added${as}.`;
  }
  return `${String(names.length)} people were added${as}.`;
}

/** A typed address, as someone pastes it: enough to say "no one has this". */
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Add several people at once, with one role for all of them (spec 196 US5).
 *
 * It took one person per open before: pick, added, closed. Filling a workspace
 * after the rollout meant opening it once per colleague. Now the people gather
 * as chips and go in together, each through the same endpoint as before — the
 * service adds one person per request, so "together" is one request each, in
 * turn, and a refusal belongs to the person it was about.
 *
 * The directory is who can be offered. Someone typed by address who is not in
 * it is said to be outside the organization before anything is sent, rather than
 * learned from a refusal after; a refusal that still comes (the directory is the
 * realm, membership is checked by the service) stays on that person's chip with
 * its reason, while everyone else is added.
 *
 * Exported for the People tab, whose header holds it (spec 194 US3).
 */
export function AddMemberPicker({
  workspaceId,
  memberIds,
}: {
  workspaceId: string;
  memberIds: readonly string[];
}) {
  const [open, setOpen] = useState(false);
  /**
   * Held open while it sends: closed mid-way, the rest would still be added, and
   * the toast would name people the dialog never showed being added.
   */
  const [sending, setSending] = useState(false);
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && sending) return;
        setOpen(next);
      }}
    >
      <DialogTrigger
        render={
          <Button variant="outline" size="sm" aria-label="Add member">
            <UserPlusIcon aria-hidden="true" />
            Add member
          </Button>
        }
      />
      <DialogContent className="sm:max-w-md">
        {/* Mounted only while open, so every opening starts empty. */}
        {open ? (
          <AddMembersForm
            workspaceId={workspaceId}
            memberIds={memberIds}
            onSendingChange={setSending}
            onDone={() => {
              setOpen(false);
            }}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function AddMembersForm({
  workspaceId,
  memberIds,
  onSendingChange,
  onDone,
}: {
  workspaceId: string;
  memberIds: readonly string[];
  onSendingChange: (sending: boolean) => void;
  onDone: () => void;
}) {
  const { people, loading, failed } = useAssignablePeople();
  const add = useAddMember();
  const [query, setQuery] = useState('');
  const [chosen, setChosen] = useState<readonly UserSummary[]>([]);
  const [role, setRole] = useState<WorkspaceRole>('member');
  /** Why each person still here was refused, by their id. */
  const [refused, setRefused] = useState<Readonly<Record<string, string>>>({});
  const [sending, setSending] = useState(false);

  const inside = useMemo(() => new Set(memberIds), [memberIds]);
  const needle = query.trim().toLowerCase();
  const candidates = useMemo(
    () =>
      people.filter(
        (person) =>
          !inside.has(person.id) &&
          (!needle ||
            person.display_name.toLowerCase().includes(needle) ||
            person.email.toLowerCase().includes(needle)),
      ),
    [people, inside, needle],
  );
  const chosenIds = new Set(chosen.map((person) => person.id));

  // A whole address that names no one offered: who it is, before it is sent.
  const typedAddress = EMAIL.test(needle) ? needle : null;
  const known = typedAddress
    ? people.find((person) => person.email.toLowerCase() === typedAddress)
    : undefined;
  const outsider =
    typedAddress !== null && known === undefined && !loading && !failed;
  const alreadyIn = known !== undefined && inside.has(known.id) ? known : null;

  function toggle(person: UserSummary) {
    setChosen((now) =>
      now.some((one) => one.id === person.id)
        ? now.filter((one) => one.id !== person.id)
        : [...now, person],
    );
    // Changing who is picked answers the refusal shown for them.
    setRefused((now) =>
      Object.fromEntries(
        Object.entries(now).filter(([id]) => id !== person.id),
      ),
    );
  }

  async function submit() {
    setSending(true);
    onSendingChange(true);
    const added: string[] = [];
    const reasons: Record<string, string> = {};
    // One at a time: each is its own request and its own log entry, and the
    // order they were picked in is the order they arrive in.
    for (const person of chosen) {
      try {
        await add.mutateAsync({ workspaceId, userId: person.id, role });
        added.push(person.display_name);
      } catch (failure) {
        reasons[person.id] =
          failure instanceof Error
            ? failure.message
            : 'This person could not be added.';
      }
    }
    setSending(false);
    onSendingChange(false);
    if (added.length > 0) toast.success(addedSentence(added, role));
    if (Object.keys(reasons).length === 0) {
      onDone();
      return;
    }
    // Who was added leaves the chips; who was refused stays, with why.
    setChosen((now) => now.filter((person) => person.id in reasons));
    setRefused(reasons);
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>Add members</DialogTitle>
        <DialogDescription>
          Pick everyone to add; they all get the same role.
        </DialogDescription>
      </DialogHeader>

      {chosen.length > 0 ? (
        <ul aria-label="To add" className="flex flex-wrap gap-1.5">
          {chosen.map((person) => {
            const reason = refused[person.id];
            return (
              <li
                key={person.id}
                aria-label={person.display_name}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-full border py-0.5 ps-1 pe-1.5 text-sm',
                  reason
                    ? 'border-destructive/60 bg-destructive/5'
                    : 'border-border bg-card',
                )}
              >
                <UserAvatar
                  id={person.id}
                  displayName={person.display_name}
                  email={person.email}
                  className="size-5"
                />
                <span className="text-foreground">{person.display_name}</span>
                <button
                  type="button"
                  aria-label={`Remove ${person.display_name}`}
                  disabled={sending}
                  onClick={() => {
                    toggle(person);
                  }}
                  className="text-muted-foreground hover:text-foreground focus-visible:ring-ring touch-hit relative rounded-full focus-visible:ring-3 focus-visible:outline-none disabled:opacity-50"
                >
                  <XIcon
                    aria-hidden="true"
                    className="size-3.5"
                    strokeWidth={1.5}
                  />
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
      {Object.keys(refused).length > 0 ? (
        <ul role="alert" className="flex flex-col gap-1">
          {chosen
            .filter((person) => person.id in refused)
            .map((person) => (
              <li key={person.id} className="text-destructive text-xs">
                {person.display_name}: {refused[person.id]}
              </li>
            ))}
        </ul>
      ) : null}

      <div className="relative">
        <SearchIcon
          aria-hidden="true"
          strokeWidth={1.5}
          className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2"
        />
        <Input
          aria-label="Search people"
          placeholder="Search by name or email…"
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
          }}
          className="ps-8"
        />
      </div>

      {outsider ? (
        <p className="text-muted-foreground flex items-start gap-1.5 text-xs">
          <UserXIcon
            aria-hidden="true"
            strokeWidth={1.5}
            className="mt-0.5 size-3.5 shrink-0"
          />
          No one in the organization has the address {typedAddress}, so they
          cannot be added. Ask an organization admin to invite them first.
        </p>
      ) : alreadyIn ? (
        <p className="text-muted-foreground text-xs">
          {alreadyIn.display_name} is already in this workspace.
        </p>
      ) : null}

      {candidates.length > 0 ? (
        <ul
          aria-label="People"
          className="border-border -mx-1 flex max-h-56 flex-col overflow-y-auto rounded-md border p-1"
        >
          {candidates.map((person) => {
            const picked = chosenIds.has(person.id);
            return (
              <li key={person.id}>
                <button
                  type="button"
                  aria-pressed={picked}
                  disabled={sending}
                  onClick={() => {
                    toggle(person);
                  }}
                  className="hover:bg-accent/50 focus-visible:ring-ring touch-target flex w-full items-center gap-2.5 rounded-sm px-2 py-1.5 text-left focus-visible:ring-3 focus-visible:outline-none disabled:opacity-50"
                >
                  <UserAvatar
                    id={person.id}
                    displayName={person.display_name}
                    email={person.email}
                    className="size-6"
                  />
                  <span className="flex min-w-0 flex-1 flex-col leading-tight">
                    <span className="text-foreground truncate text-sm">
                      {person.display_name}
                    </span>
                    <span className="text-muted-foreground truncate text-xs">
                      {person.email}
                    </span>
                  </span>
                  <CheckIcon
                    aria-hidden="true"
                    strokeWidth={1.5}
                    className={cn(
                      'text-foreground size-4 shrink-0',
                      !picked && 'invisible',
                    )}
                  />
                </button>
              </li>
            );
          })}
        </ul>
      ) : outsider || alreadyIn !== null ? null : (
        <p className="text-muted-foreground text-sm">
          {loading
            ? 'Loading people…'
            : failed
              ? 'The list of people could not be loaded. Try again shortly.'
              : needle
                ? `No one matches “${query.trim()}”.`
                : 'Everyone is already in this workspace.'}
        </p>
      )}

      <DialogFooter className="sm:items-center sm:justify-between">
        <span className="text-muted-foreground flex items-center gap-2 text-sm">
          Add as
          <Select
            value={role}
            disabled={sending}
            onValueChange={(next: string | null) => {
              if (next === 'owner' || next === 'member') setRole(next);
            }}
          >
            <SelectTrigger
              aria-label="Role for everyone added"
              className="touch-target w-28"
            >
              <SelectValue>
                {(value: string) => (value === 'owner' ? 'Owner' : 'Member')}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="member">Member</SelectItem>
              <SelectItem value="owner">Owner</SelectItem>
            </SelectContent>
          </Select>
        </span>
        <Button
          disabled={chosen.length === 0 || sending}
          onClick={() => {
            void submit();
          }}
        >
          {sending ? 'Adding…' : addPeopleLabel(chosen.length)}
        </Button>
      </DialogFooter>
    </>
  );
}
